import { useState } from 'react';
import { ArrowRight, Check, CreditCard, Building2 } from 'lucide-react';
import { Link } from '@tanstack/react-router';
import { Avatar } from '../components/Avatar';
import { Button } from '../components/Button';
import { Card, CardHeader } from '../components/Card';
import { Badge } from '../components/Badge';
import { Money } from '../components/Money';
import { Modal } from '../components/Modal';
import { PageHeader } from '../components/PageHeader';
import { cn } from '../lib/cn';
import { formatDate, formatInt } from '../lib/format';
import { useActiveBusiness, useBusinesses, useSubscription, useSubscriptionsMap } from '../api/hooks';
import { useBrand } from '../brand/BrandProvider';

type CancelReason = 'too_expensive' | 'missing_feature' | 'switching_tools' | 'closing' | 'other';

const CANCEL_REASONS: Array<{ value: CancelReason; label: string }> = [
  { value: 'too_expensive', label: 'Too expensive' },
  { value: 'missing_feature', label: 'Missing a feature' },
  { value: 'switching_tools', label: 'Switching tools' },
  { value: 'closing', label: 'Closing the business' },
  { value: 'other', label: 'Other' },
];

export function Subscription() {
  const brand = useBrand();
  const { data: currentBusiness } = useActiveBusiness();
  const { data: businessesData } = useBusinesses();
  const { data: subscription } = useSubscription(currentBusiness?.id ?? '');
  const { data: subscriptionsMap } = useSubscriptionsMap();

  const [changePlanOpen, setChangePlanOpen] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [pmOpen, setPmOpen] = useState(false);
  const [pmStep, setPmStep] = useState<'choose' | 'redirecting' | 'done'>('choose');
  const [reason, setReason] = useState<CancelReason | null>(null);
  const [otherReason, setOtherReason] = useState('');
  const [confirmText, setConfirmText] = useState('');
  const canConfirmCancel = confirmText === 'CANCEL' && reason !== null;

  function startPmFlow() {
    setPmStep('choose');
    setPmOpen(true);
  }
  function pmRedirect() {
    setPmStep('redirecting');
    setTimeout(() => setPmStep('done'), 1500);
  }
  function closePm() {
    setPmOpen(false);
    setTimeout(() => setPmStep('choose'), 250);
  }

  if (!currentBusiness) {
    return (
      <div className="px-4 sm:px-6 lg:px-8 py-10 text-center text-sm text-zinc-500 dark:text-zinc-400">
        Loading subscription…
      </div>
    );
  }
  if (!subscription) {
    return (
      <div className="px-4 sm:px-6 lg:px-8 py-6 sm:py-8 max-w-[1440px] mx-auto">
        <PageHeader eyebrow={`Subscription for ${currentBusiness.name}`} title="No active subscription" subtitle="This business doesn't have an active plan yet." />
        <Card className="p-6 sm:p-8 text-center">
          <p className="text-sm text-zinc-600 dark:text-zinc-300 mb-4">Choose a plan to activate this business's subscription.</p>
          <Link to="/change-plan"><Button>Choose a plan</Button></Link>
        </Card>
      </div>
    );
  }
  const allBusinesses = businessesData ?? [];
  const otherBusinesses = allBusinesses.filter((b) => b.id !== currentBusiness.id);

  const STATUS_TONE = {
    active: 'success' as const,
    trial: 'info' as const,
    past_due: 'warning' as const,
    suspended: 'error' as const,
    canceled: 'neutral' as const,
  };
  const STATUS_LABEL = {
    active: 'Active',
    trial: 'Trial',
    past_due: 'Past due',
    suspended: 'Suspended',
    canceled: 'Canceled',
  };

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-6 sm:py-8 max-w-[1440px] mx-auto">
      <PageHeader
        eyebrow={`Subscription for ${currentBusiness.name}`}
        title={
          <span className="flex items-center gap-3 flex-wrap">
            <span className={cn('w-9 h-9 rounded-md grid place-items-center flex-shrink-0', currentBusiness.iconColor)}>
              <Building2 className="w-5 h-5 text-white dark:text-zinc-950" />
            </span>
            <span>{currentBusiness.name}</span>
            <span className="text-base font-normal text-zinc-500 dark:text-zinc-400 mono">
              · {currentBusiness.id}
            </span>
          </span>
        }
        subtitle={
          <span>
            Plan and billing for this business. Each business has its own subscription —{' '}
            <Link to="/businesses" className="font-medium text-brand dark:text-brand-dm hover:underline">
              switch business
            </Link>{' '}
            to see another.
          </span>
        }
      />

      <div className="grid lg:grid-cols-3 gap-3 sm:gap-4">
        {/* Current plan */}
        <Card className="lg:col-span-2 p-5 sm:p-6">
          <CardHeader
            title={
              <span className="flex items-center gap-2.5 flex-wrap">
                {subscription.plan}
                <span className="text-zinc-400 dark:text-zinc-500">·</span>
                <Money minor={subscription.amount} hideCents tone="neutral" />
                <span className="text-zinc-500 dark:text-zinc-400 text-sm font-normal">/ {subscription.interval}</span>
                {subscription.plan === 'Pro' && (
                  <span className="ml-1 text-xs font-normal text-zinc-500 dark:text-zinc-400">
                    ← grandfathered (new price ৳1,700 from 01 Jul)
                  </span>
                )}
              </span>
            }
            subtitle={
              <span>
                Started {formatDate(subscription.startedAt)} · Renews{' '}
                <span className="tabular-nums font-medium text-zinc-700 dark:text-zinc-200">
                  {formatDate(subscription.renewsAt)}
                </span>
              </span>
            }
            action={<Badge tone={STATUS_TONE[subscription.status]}>{STATUS_LABEL[subscription.status]}</Badge>}
          />

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 mt-2">
            <UsageBar label="Locations" used={subscription.usage.locations.used} limit={subscription.usage.locations.limit} />
            <UsageBar label="Users" used={subscription.usage.users.used} limit={subscription.usage.users.limit} />
            <UsageBar label="Products" used={subscription.usage.products.used} limit={subscription.usage.products.limit} compact />
          </div>

          <div className="mt-6 pt-5 border-t border-zinc-200 dark:border-zinc-700 flex flex-wrap items-center gap-2">
            <Button variant="secondary" size="sm" onClick={() => setChangePlanOpen(true)}>
              Change plan
            </Button>
            <Button variant="ghost" size="sm" onClick={() => setCancelOpen(true)}>
              Cancel subscription
            </Button>
          </div>
        </Card>

        {/* Payment method */}
        <Card className="p-5 sm:p-6">
          <CardHeader title="Payment method" />
          <div className="flex items-center gap-3 p-4 rounded-lg bg-zinc-50 dark:bg-zinc-800/40 border border-zinc-200 dark:border-zinc-700">
            <span className="w-10 h-10 grid place-items-center rounded-md bg-white dark:bg-zinc-950 border border-zinc-200 dark:border-zinc-700">
              <CreditCard className="w-5 h-5 text-zinc-500 dark:text-zinc-400" />
            </span>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium text-zinc-950 dark:text-zinc-50">SSLCommerz · auto-charge</div>
              <div className="text-xs text-zinc-500 dark:text-zinc-400 mono tabular-nums">
                Last charge: 03 May · ৳1,500
              </div>
            </div>
          </div>
          <Button variant="secondary" size="sm" fullWidth className="mt-3" onClick={startPmFlow}>
            Update payment method
          </Button>
        </Card>
      </div>

      {/* Other businesses' subscriptions */}
      {otherBusinesses.length > 0 && (
        <Card className="mt-4 p-5 sm:p-6">
          <CardHeader
            title="Your other businesses"
            subtitle={`Each of your ${allBusinesses.length} businesses has its own subscription`}
            action={
              <Link
                to="/businesses"
                className="text-xs font-medium text-brand dark:text-brand-dm hover:underline inline-flex items-center gap-1"
              >
                Manage all <ArrowRight className="w-3 h-3" />
              </Link>
            }
          />
          <div className="grid sm:grid-cols-2 gap-3">
            {otherBusinesses.map((b) => {
              const sub = subscriptionsMap?.[b.id];
              if (!sub) return null;
              return (
                <Link
                  key={b.id}
                  to="/businesses"
                  className="group flex items-center gap-3 p-3 rounded-lg border border-zinc-200 dark:border-zinc-600 hover:border-zinc-300 dark:hover:border-zinc-600 hover:bg-zinc-50 dark:hover:bg-zinc-800/50 transition"
                >
                  <Avatar name={b.name} size="md" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-medium text-zinc-950 dark:text-zinc-50 truncate">
                        {b.name}
                      </span>
                      <Badge tone={STATUS_TONE[sub.status]}>{STATUS_LABEL[sub.status]}</Badge>
                    </div>
                    <div className="text-xs text-zinc-500 dark:text-zinc-400 mt-0.5 tabular-nums">
                      {sub.plan} ·{' '}
                      <Money minor={sub.amount} hideCents tone="neutral" className="text-zinc-700 dark:text-zinc-200" />
                      {' '}/ {sub.interval} · renews {formatDate(sub.renewsAt, { compact: true })}
                    </div>
                  </div>
                  <ArrowRight className="w-4 h-4 text-zinc-400 dark:text-zinc-500 group-hover:text-zinc-700 dark:group-hover:text-zinc-200 flex-shrink-0" />
                </Link>
              );
            })}
          </div>
        </Card>
      )}

      {/* What's included */}
      <Card className="mt-4 p-5 sm:p-6">
        <CardHeader
          title={`What's included in ${subscription.plan}`}
          subtitle="Compare against other plans"
        />
        <div className="grid sm:grid-cols-2 gap-y-2 gap-x-6">
          {[
            'Up to 5 business locations',
            'Up to 15 user accounts',
            'Up to 10,000 products',
            `Custom subdomain (.${brand.domain})`,
            'Custom domain (CNAME)',
            'Dedicated MySQL database',
            'Priority email support',
            'WooCommerce sync',
            'CRM module · HRM module',
            'Manufacturing & repair modules',
          ].map((item) => (
            <div key={item} className="flex items-center gap-2 text-sm text-zinc-700 dark:text-zinc-200">
              <Check className="w-4 h-4 text-emerald-600 dark:text-emerald-400 flex-shrink-0" />
              {item}
            </div>
          ))}
        </div>
      </Card>

      {/* Change plan modal */}
      <Modal
        open={changePlanOpen}
        onClose={() => setChangePlanOpen(false)}
        title="Change plan"
        size="lg"
        footer={
          <>
            <Button variant="ghost" onClick={() => setChangePlanOpen(false)}>
              Cancel
            </Button>
            <Link to="/change-plan">
              <Button>Continue</Button>
            </Link>
          </>
        }
      >
        <div className="space-y-3">
          <p className="text-sm text-zinc-600 dark:text-zinc-300">
            Currently <strong>Pro · ৳1,500/mo</strong>. 12 days left in current period.
          </p>
          <PlanOption value="basic" name="Basic" price={50000} description="2 locations · 5 users · 2,000 products" />
          <PlanOption value="pro" name="Pro" price={150000} description="5 locations · 15 users · 10,000 products" current popular />
          <PlanOption value="enterprise" name="Enterprise" price={null} description="Unlimited everything · self-hosted DB · dedicated support" />
        </div>
        <div className="mt-5 p-4 rounded-lg bg-zinc-50 dark:bg-zinc-800/40 border border-zinc-200 dark:border-zinc-700">
          <div className="flex items-center justify-between text-sm">
            <span className="text-zinc-700 dark:text-zinc-200">Net charge today</span>
            <Money minor={38400} tone="neutral" className="font-medium text-zinc-950 dark:text-zinc-50" />
          </div>
          <p className="text-xs text-zinc-500 dark:text-zinc-400 mt-1.5">
            Prorated for the remaining 12 days. Coupon EID2026 (20% off, 3 months) will apply on next renewal.
          </p>
        </div>
      </Modal>

      {/* Cancel modal */}
      <Modal
        open={cancelOpen}
        onClose={() => setCancelOpen(false)}
        title="Cancel subscription"
        size="lg"
        footer={
          <>
            <Button variant="secondary" onClick={() => setCancelOpen(false)}>
              Keep subscription
            </Button>
            <Button
              variant="destructive"
              disabled={!canConfirmCancel}
              onClick={() => setCancelOpen(false)}
            >
              Cancel subscription
            </Button>
          </>
        }
      >
        <p className="text-sm text-zinc-700 dark:text-zinc-200">
          We're sorry to see you go. Help us improve by sharing why.
        </p>

        <fieldset className="mt-4">
          <legend className="text-sm font-medium text-zinc-900 dark:text-zinc-100 mb-2">
            Reason for leaving
          </legend>
          <div className="space-y-2">
            {CANCEL_REASONS.map((r) => (
              <label
                key={r.value}
                className={cn(
                  'flex items-center gap-3 px-3 py-2 rounded-md border cursor-pointer transition',
                  reason === r.value
                    ? 'border-brand bg-brand-subtle dark:border-brand-dm dark:bg-brand-dm-subtle/30'
                    : 'border-zinc-200 dark:border-zinc-700 hover:border-zinc-300 dark:hover:border-zinc-600',
                )}
              >
                <input
                  type="radio"
                  name="cancel-reason"
                  value={r.value}
                  checked={reason === r.value}
                  onChange={() => setReason(r.value)}
                  className="text-brand focus:ring-brand"
                />
                <span className="text-sm text-zinc-900 dark:text-zinc-100">{r.label}</span>
              </label>
            ))}
          </div>
          {reason === 'other' && (
            <textarea
              value={otherReason}
              onChange={(e) => setOtherReason(e.target.value)}
              placeholder="Tell us a bit more (optional)…"
              rows={2}
              className="mt-3 block w-full px-3 py-2.5 rounded-md text-sm bg-white dark:bg-zinc-900 text-zinc-950 dark:text-zinc-50 border border-zinc-300 dark:border-zinc-700 focus:outline-none focus:ring-2 focus:ring-brand focus:border-brand dark:focus:ring-brand-dm dark:focus:border-brand-dm transition resize-none"
            />
          )}
        </fieldset>

        <div className="mt-5 rounded-lg bg-zinc-50 dark:bg-zinc-800/40 border border-zinc-200 dark:border-zinc-700 p-4">
          <div className="text-sm font-medium text-zinc-900 dark:text-zinc-100 mb-2">
            What happens next
          </div>
          <ul className="space-y-1.5 text-sm text-zinc-600 dark:text-zinc-300">
            <li className="flex gap-2">
              <span className="text-zinc-400 dark:text-zinc-500">·</span>
              Your POS keeps working until <strong className="text-zinc-800 dark:text-zinc-100">{formatDate(subscription.renewsAt)}</strong> (period end).
            </li>
            <li className="flex gap-2">
              <span className="text-zinc-400 dark:text-zinc-500">·</span>
              After that, your account is suspended for 30 days. You can reactivate any time.
            </li>
            <li className="flex gap-2">
              <span className="text-zinc-400 dark:text-zinc-500">·</span>
              After 30 days suspended, your data is permanently deleted.
            </li>
            <li className="flex gap-2">
              <span className="text-zinc-400 dark:text-zinc-500">·</span>
              You can export everything as JSON before then.
            </li>
          </ul>
        </div>

        <div className="mt-5">
          <label className="block text-sm font-medium text-zinc-900 dark:text-zinc-100 mb-1.5">
            Type <span className="mono font-semibold">CANCEL</span> to confirm
          </label>
          <input
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            placeholder="CANCEL"
            className="block w-full px-3 py-2.5 rounded-md text-sm bg-white dark:bg-zinc-900 text-zinc-950 dark:text-zinc-50 border border-zinc-300 dark:border-zinc-700 focus:outline-none focus:ring-2 focus:ring-rose-500 focus:border-rose-500 transition mono tabular-nums uppercase"
            autoComplete="off"
          />
        </div>
      </Modal>

      {/* Update payment method modal */}
      <Modal
        open={pmOpen}
        onClose={closePm}
        title={pmStep === 'done' ? 'Payment method updated' : 'Update payment method'}
        size="md"
        footer={
          pmStep === 'choose' ? (
            <>
              <Button variant="ghost" onClick={closePm}>Cancel</Button>
              <Button onClick={pmRedirect}>Continue to SSLCommerz</Button>
            </>
          ) : pmStep === 'redirecting' ? (
            <Button variant="ghost" onClick={closePm}>Close</Button>
          ) : (
            <Button onClick={closePm}>Done</Button>
          )
        }
      >
        {pmStep === 'choose' && (
          <div className="space-y-3">
            <p className="text-sm text-zinc-700 dark:text-zinc-200">
              You'll be redirected to SSLCommerz to securely save a new card or mobile-banking account.
              We never see or store your card details — only a tokenized reference.
            </p>
            <div className="rounded-lg border border-zinc-200 dark:border-zinc-600 p-3 text-sm">
              <div className="flex items-center gap-2.5">
                <CreditCard className="w-4 h-4 text-zinc-500" />
                <span className="text-zinc-700 dark:text-zinc-200">Currently saved: SSLCommerz · auto-charge</span>
              </div>
            </div>
            <p className="text-xs text-zinc-500 dark:text-zinc-400">
              A ৳1 verification charge may be authorized and refunded automatically.
            </p>
          </div>
        )}
        {pmStep === 'redirecting' && (
          <div className="text-center py-8">
            <div className="w-10 h-10 mx-auto rounded-full border-2 border-zinc-300 dark:border-zinc-700 border-t-brand dark:border-t-brand-dm animate-spin mb-4" />
            <p className="text-sm text-zinc-700 dark:text-zinc-200">
              Redirecting to SSLCommerz checkout…
            </p>
          </div>
        )}
        {pmStep === 'done' && (
          <div className="text-center py-6">
            <div className="w-12 h-12 mx-auto rounded-full bg-emerald-100 dark:bg-emerald-950/40 grid place-items-center text-emerald-700 dark:text-emerald-300 mb-4">
              <Check className="w-6 h-6" />
            </div>
            <h3 className="text-base font-semibold text-zinc-950 dark:text-zinc-50">All set</h3>
            <p className="mt-1 text-sm text-zinc-600 dark:text-zinc-300">
              Your new payment method is saved. We'll auto-charge it on the next renewal.
            </p>
          </div>
        )}
      </Modal>
    </div>
  );
}

function UsageBar({
  label,
  used,
  limit,
  compact = false,
}: {
  label: string;
  used: number;
  limit: number;
  compact?: boolean;
}) {
  const pct = Math.min(100, Math.round((used / limit) * 100));
  return (
    <div>
      <div className="flex items-center justify-between mb-1.5">
        <span className="text-xs font-medium text-zinc-700 dark:text-zinc-200">{label}</span>
        <span className="text-xs text-zinc-500 dark:text-zinc-400 tabular-nums">
          {compact ? `${formatInt(used)} / ${formatInt(limit)}` : `${used} / ${limit}`}
        </span>
      </div>
      <div className="h-1.5 bg-zinc-100 dark:bg-zinc-800 rounded-full overflow-hidden">
        <div
          className={cn(
            'h-full rounded-full transition-[width] duration-500 ease-out',
            pct > 90 ? 'bg-rose-500' : pct > 75 ? 'bg-amber-500' : 'bg-brand dark:bg-brand-dm',
          )}
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}

function PlanOption({
  name,
  price,
  description,
  current = false,
  popular = false,
}: {
  value: string;
  name: string;
  price: number | null;
  description: string;
  current?: boolean;
  popular?: boolean;
}) {
  return (
    <label
      className={cn(
        'flex items-start gap-3 p-4 rounded-lg border-2 cursor-pointer transition',
        current
          ? 'border-brand bg-brand-subtle dark:border-brand-dm dark:bg-brand-dm-subtle/30'
          : 'border-zinc-200 dark:border-zinc-700 hover:border-zinc-300 dark:hover:border-zinc-600',
      )}
    >
      <input
        type="radio"
        name="plan"
        defaultChecked={current}
        className="mt-1 text-brand focus:ring-brand"
      />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-medium text-zinc-950 dark:text-zinc-50">{name}</span>
          {popular && (
            <span className="px-1.5 py-px rounded text-[10px] font-medium bg-brand text-white dark:bg-brand-dm dark:text-zinc-950">
              Most popular
            </span>
          )}
          {current && <span className="text-xs text-zinc-500 dark:text-zinc-400">(your plan)</span>}
        </div>
        <p className="text-xs text-zinc-600 dark:text-zinc-300 mt-0.5">{description}</p>
      </div>
      <div className="text-right flex-shrink-0">
        {price !== null ? (
          <>
            <Money minor={price} hideCents tone="neutral" className="font-medium text-zinc-950 dark:text-zinc-50" />
            <div className="text-xs text-zinc-500 dark:text-zinc-400">/ month</div>
          </>
        ) : (
          <span className="text-sm font-medium text-zinc-950 dark:text-zinc-50">Custom</span>
        )}
      </div>
    </label>
  );
}
